package com.kaihuoy.home.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.kaihuoy.shared.data.User
import com.kaihuoy.shared.data.Machine
import com.kaihuoy.shared.data.ServiceRecord
import com.kaihuoy.components.common.*
import com.kaihuoy.components.common.*
import com.kaihuoy.web.router.AppRouterState
import com.kaihuoy.web.FirebaseService
import kotlin.js.Date
import kotlinx.coroutines.flow.first

@Composable
fun NewServiceScreen(user: User?) {
    val scope = rememberCoroutineScope()
    var machines by remember { mutableStateOf<List<Machine>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var isSaving by remember { mutableStateOf(false) }

    // Lomakkeen kentät
    var selectedMachineId by remember { mutableStateOf("") }
    var serviceType by remember { mutableStateOf("Huolto") }
    var machineHours by remember { mutableStateOf("") }
    var serviceTarget by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }

    // Haetaan koneet
    LaunchedEffect(Unit) {
        scope.launch {
            try {
                val snapshot = FirebaseService.database.reference("machines").valueEvents.first()
                machines = snapshot.children.mapNotNull {
                    try { it.value<Machine>() } catch (e: Exception) { null }
                }
                isLoading = false
            } catch (e: Exception) {
                println("Koneiden haku epäonnistui: ${e.message}")
                isLoading = false
            }
        }
    }

    Div(attrs = {
        style { padding(32.px, screenContentPadding()); maxWidth(800.px); margin(0.px) }
    }) {
        // Takaisin
        Div(attrs = {
            style { marginBottom(16.px); cursor("pointer"); color(KaihuTheme.PrimaryTeal); fontWeight("600") }
            onClick { AppRouterState.navigate("huolto") }
        }) { Text("← Takaisin huoltoon") }

        Div(attrs = {
            style {
                backgroundColor(KaihuTheme.Surface); padding(32.px); borderRadius(16.px)
                property("box-shadow", KaihuTheme.ShadowMd)
            }
        }) {
            H1(attrs = { style { marginTop(0.px); marginBottom(8.px); color(KaihuTheme.TextPrimary); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) {
                TIcon("wrench"); Text(" Uusi huoltotyö")
            }
            P(attrs = { style { color(KaihuTheme.TextSecondary); marginBottom(32.px); fontSize(1.05.em) } }) {
                Text("Kirjaa uusi huolto tai korjaus")
            }

            if (isLoading) {
                P { Text("Ladataan koneita...") }
                return@Div
            }

            // Konevalinta
            Div(attrs = { style { marginBottom(16.px) } }) {
                StyledDropdown(
                    value = selectedMachineId,
                    options = listOf(DropdownOption("", "-- Valitse kone --")) + machines.map { m ->
                        DropdownOption(m.machineNumber, "${m.machineNumber} - ${m.manufacturer} ${m.model}")
                    },
                    placeholder = "-- Valitse kone --",
                    label = "Kone *",
                    onSelect = { selectedMachineId = it }
                )
            }

            // Valitun koneen tiedot
            val selectedMachine = machines.find { it.machineNumber == selectedMachineId }
            if (selectedMachine != null) {
                Div(attrs = {
                    style {
                        padding(12.px); borderRadius(10.px); backgroundColor(Color("#E6FFFA"))
                        marginBottom(16.px); fontSize(0.9.em)
                    }
                }) {
                    Span(attrs = { style { fontWeight("600") } }) {
                        Text("${selectedMachine.manufacturer} ${selectedMachine.model}")
                    }
                    Text("  •  Käyttötunnit: ")
                    Span(attrs = { style { fontWeight("700"); color(KaihuTheme.PrimaryTeal) } }) {
                        Text("${selectedMachine.currentHours} h")
                    }
                }
            }

            // Huoltotyyppi
            Div(attrs = { style { marginBottom(16.px) } }) {
                StyledDropdown(
                    value = serviceType,
                    options = listOf("Huolto", "Korjaus", "Tarkastus", "Määräaikaishuolto", "Öljynvaihto").toDropdownOptions(),
                    label = "Huollon tyyppi *",
                    onSelect = { serviceType = it }
                )
            }

            // Huoltokohde
            StyledInput(
                label = "Huoltokohde",
                value = serviceTarget,
                placeholder = "esim. Moottori, Hydrauliikka, Jarrut...",
                onValueChange = { serviceTarget = it }
            )

            // Käyttötunnit
            StyledInput(
                label = "Käyttötunnit huoltohetkellä",
                value = machineHours,
                placeholder = "esim. 4500",
                onValueChange = { machineHours = it }
            )

            // Kuvaus
            Div(attrs = { style { marginBottom(16.px) } }) {
                Label(attrs = {
                    style { display(DisplayStyle.Block); fontWeight("500"); marginBottom(6.px); fontSize(0.875.em) }
                }) { Text("Kuvaus / huomiot") }


                TextArea(value = description, attrs = {
                    onInput { description = it.value }
                    style {
                        width(100.percent); padding(12.px, 16.px); borderRadius(8.px)
                        property("border", "1px solid ${KaihuTheme.Border}"); fontSize(1.em)
                        boxSizing("border-box"); property("min-height", "80px")
                    }
                    attr("placeholder", "Mitä tehtiin, mitä osia vaihdettiin jne.")
                })
            }

            // Tallenna
            Div(attrs = {
                style {
                    marginTop(32.px); display(DisplayStyle.Flex); gap(16.px)
                    property("border-top", "1px solid ${KaihuTheme.Divider}"); paddingTop(24.px)
                }
            }) {
                StyledButton(
                    text = "← Peruuta",
                    onClick = { AppRouterState.navigate("huolto") },
                    backgroundColor = KaihuTheme.Grey
                )
                StyledButton(
                    text = if (isSaving) "Tallennetaan..." else "💾 Tallenna huolto",
                    enabled = !isSaving,
                    onClick = {
                        if (selectedMachineId.isBlank()) {
                            com.kaihuoy.components.common.AppFeedback.showWarning("Valitse kone!")
                            return@StyledButton
                        }
                        isSaving = true
                        scope.launch {
                            try {
                                val ref = FirebaseService.database.reference("services").push()
                                val record = ServiceRecord(
                                    id = ref.key ?: "",
                                    userName = user?.displayName ?: "",
                                    serviceType = serviceType,
                                    machineHours = machineHours,
                                    equipmentId = selectedMachineId,
                                    serviceDate = Date.now().toLong(),
                                    userId = user?.uid ?: ""
                                )
                                ref.setValue(record)
                                com.kaihuoy.components.common.AppFeedback.showSuccess("Huolto tallennettu onnistuneesti!")
                                AppRouterState.navigate("huolto")
                            } catch (e: Exception) {
                                com.kaihuoy.components.common.AppFeedback.showError("Tallennus epäonnistui: ${e.message}")
                                isSaving = false
                            }
                        }
                    },
                    backgroundColor = KaihuTheme.Green
                )
            }
        }
    }
}